import React, { useCallback, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from "react-router-dom";

import { makeStyles } from '@material-ui/core/styles';
import { AppBar, Toolbar, Tooltip, Typography} from '@material-ui/core';

import api from '../../utils/api'
import { setClean } from '../../actions';

import { Markup } from '../Templates'
import { MuiRouterLink, LoadingButton } from '../CustomMui';
import { InteractiveSpeedDial } from '.'
import LockContext from '../../contexts/LockContext';
import LastEditedContext from '../../contexts/LastEditedContext';


const root = process.env.REACT_APP_API_ROOT || '';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  appBar: {
    backgroundColor: '#fff',
    borderTop: '1px solid ' + theme.palette.neutral.light1,
    borderBottom: '1px solid ' + theme.palette.neutral.light1,
  },
  link: {
    fontSize: 18,
    marginRight: 26
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    fontWeight: 500,
  },
  previewBtnWrapper: {
    marginRight: 68,
    marginLeft: 10,
  },
  pipe: {
    fontWeight: 400,
    color: theme.palette.neutral.light2,
  },
  lockIcon: {
    margin: '0px 25px',
    fontSize: 22,
    color: theme.palette.neutral.dark1,
  }
}));


export const InteractiveToolbar = React.memo(({ name,setNotification,template,title, ...props}) => {
  const { id } = useParams();
  const classes = useStyles();
  const history = useHistory();

  const { lock,setLock } = useContext(LockContext);
  const { setLastEdited } = useContext(LastEditedContext);
  
  const dispatch = useDispatch();
  const dirty = useSelector(state => state.dirty);
  const interactive = useSelector(state => state.interactive);

  const [createTemplate, setCreateTemplate] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);


  const isLocked = !!lock.owner;

  // Trigger hidden Markup component to render and call saveInteractive
  const generateMarkup = (preview) => {
    // if there is no need to save, just open preview in new tab
    if ((!dirty && preview) || (isLocked && !lock.isCurrent)) {
      window.open(`${root}/preview/${id}`)
      return;
    }

    // Set appropriate button's loading state
    if (preview) {
      setPreviewLoading(true)
    }
    else {
      setSaveLoading(true)
    }

    // Triggers Markup to "render" and trigger save.
    setCreateTemplate(true)
  }

  // Parses markup and sends all interactive data points to BE
  const saveInteractive = useCallback((templateRef) => {
    const openInNewTab = previewLoading;
    const elem = templateRef.current;    

    let template = elem.cloneNode(true);

    let imgsToReplace = template.querySelectorAll('[data-src]');


    for ( let img of imgsToReplace ) {
      // make sure data-src isn't present but empty
      if (!img.dataset.src) {
        continue;
      }

      if (img.nodeName === "IMG") {
        img.src = img.dataset.src;
      }
      else {
        img.style.backgroundImage = `url(${img.dataset.src})`;
      }

      delete img.dataset.src;

    }

    // Make template out of innerHTML
    let templateString = template.innerHTML;
    // innerHTML encodes ' inside "" -> turn back into ', just for url('sometext')
    templateString = templateString.replace(/(url\()&quot;(.*?)&quot;(\))/g, "$1'$2'$3")

    const data = {
      uuid: interactive.uuid,
      type: name,
      title: interactive.title,
      template: templateString,
      interactiveData: JSON.stringify(interactive),
      lockUuid: lock.id,
    }


    const handleSaveSuccess = (id, lastEdited) => {

      setNotification({
        open: true,
        severity: 'success',
        message: 'Item successfully saved.',
      })

      setLock({
        id: '',
        owner: '',
        ownerEmail: '',
      })

      setLastEdited(lastEdited)


      dispatch(setClean(interactive.uuid))

      if (openInNewTab) {
        window.open(`${root}/preview/${id}`)
      }

    }

    const handleSaveError = (err) => {
      if (err.response) {
        console.log(err.response)
      } else {
        console.log(err)
      }

      let message = 'An error occurred. This item was not saved.';

      switch (err.response.status) {
        case 403: 
          // Either the item is unlocked, or you don't have a lock
          message = 'Can\'t save. Your lock has been invalidated.';
          break;
        case 423:
          // Someone has released your lock and a new one has been placed
          message = 'Can\'t save. This item is locked for edits by another user or in another window.';
          break;
        case 500:
          message = 'An internal server error occurred. This item was not saved.';
          break;
        default:
          break;
      }

      setNotification({
        open: true,
        severity: 'error',
        message: message,
      })
    }

    const handleSaveFinally = () => {
      // Removes Markup from DOM
      setCreateTemplate(false)

      // Remove loading indicators
      setPreviewLoading(false)
      setSaveLoading(false)  
    }

    // Update existing interactive, if we have an id
    if (id) {
      api.updateInteractive(id, data)
        .then((res) => {
          console.log(res.data)
          handleSaveSuccess(id, res.data.lastEdited)
        })
        .catch(err => {
          handleSaveError(err)
        })
        .finally(() => {
          handleSaveFinally()
        })
    } 
    // Create new interactive, if we don't have an id
    else {
      api.createInteractive(data)
        .then((res) => {
          handleSaveSuccess(res.data.id, res.data.lastEdited);
          history.push(`/${name}/${res.data.id}`, {dirty:false})
        })
        .catch(err => {
          handleSaveError(err)
        })
        .finally(() => {
          handleSaveFinally()        
        })
    }
  }, [dispatch, history, id, interactive, lock.id, name, previewLoading, setLastEdited, setLock, setNotification ]);


  // Helper function to display locked icon & tooltip when interactive is locked
  const renderLockedIcon = () => {

    if (!isLocked) {
      return '';
    }

    const icon = 'fa-lock';
    let title = `Item is locked for edits by ${lock.owner}.`

    if (lock.isCurrent) {
      title=`You have locked this item for edits.`
    }
    
    return ( 
      <Tooltip title={title}>
        <div className={classes.lockIcon}>
          <i className={`fas ${icon}`}></i>
        </div>
      </Tooltip>
    )
  }
  
  return (
    <div className={classes.root}>
    
      <AppBar position="relative" className={classes.appBar} color="transparent" elevation={0}>
        <Toolbar>

          {/* Back link */}
          <Tooltip title="Return home">
            <MuiRouterLink to='/' className={classes.link}><i className="fas fa-arrow-left"></i></MuiRouterLink>
          </Tooltip>

          {/* Title */}
          <Typography component="h1" variant="h4" className={classes.title} noWrap={true}>
            {title}
            {interactive.title ?
              <>
                <span className={classes.pipe}> |</span> {interactive.title.trim()}
              </>
            : '' }
          </Typography>

          {/* Lock icon */}
          {renderLockedIcon()}

          {/* Save button */}
          <Tooltip title={isLocked && !lock.isCurrent ? 'Cannot save. Item is locked for edits.' : dirty ? "Save changes" : "No changes to save"}>
            <span>
              <LoadingButton 
                buttonProps={{
                  variant:"outlined",
                  color:"primary",
                  onClick: () => generateMarkup(false),
                  disabled: !dirty || (isLocked && !lock.isCurrent)
                }}
                loading={saveLoading}
              >
                Save
              </LoadingButton>
            </span>
          </Tooltip>

          {/* Preview button */}
          <Tooltip title={!dirty && !id ? 'Nothing to preview' : isLocked ? "Preview in a new tab" : "Save changes and preview in a new tab"}>
            <span className={classes.previewBtnWrapper}>
              <LoadingButton
                buttonProps={{
                  color: "primary",
                  onClick: () => generateMarkup(true),
                  disabled: !dirty && !id
                }}
                loading={previewLoading}
                
              >
                Preview&nbsp;&nbsp;<i className="fas fa-external-link-alt"></i>
              </LoadingButton>
            </span>
          </Tooltip>

          {/* Speed dial */}
          <InteractiveSpeedDial setNotification={setNotification} />

        </Toolbar>
      </AppBar>

      {/* Hidden template */}
      {createTemplate ? 
        <Markup saveInteractive={saveInteractive} template={template}/>
      : '' }

    </div>
  )
})